import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  fetchSupportFeedback as fetchSupportFeedbackRequest,
  replyToSupportFeedback as replyToSupportFeedbackRequest,
  submitSupportFeedback as submitSupportFeedbackRequest,
  updateSupportFeedbackStatus as updateSupportFeedbackStatusRequest,
  type SubmitSupportFeedbackPayload,
  type SupportFeedbackSubmission,
} from '@services/supportFeedbackService';
import { CACHE_DURATION } from '../utils/cache';
import type { RootState } from '../index';

interface SupportFeedbackState {
  items: SupportFeedbackSubmission[];
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  lastFetched: number | null;
}

const initialState: SupportFeedbackState = {
  items: [],
  isLoading: false,
  isSaving: false,
  error: null,
  lastFetched: null,
};

const sortItems = (items: SupportFeedbackSubmission[]) =>
  [...items].sort(
    (left, right) =>
      Date.parse(right.updatedAt || right.createdAt) - Date.parse(left.updatedAt || left.createdAt)
  );

export const fetchSupportFeedback = createAsyncThunk(
  'supportFeedback/fetchAll',
  async (options: { force?: boolean } | undefined, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const { items, lastFetched } = state.supportFeedback;

    if (
      !options?.force &&
      items.length > 0 &&
      lastFetched &&
      Date.now() - lastFetched < CACHE_DURATION
    ) {
      return null;
    }

    try {
      const response = await fetchSupportFeedbackRequest();
      return sortItems(response || []);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load support tickets';
      return rejectWithValue(message);
    }
  }
);

export const submitSupportFeedback = createAsyncThunk(
  'supportFeedback/submit',
  async (payload: SubmitSupportFeedbackPayload, { rejectWithValue }) => {
    try {
      return await submitSupportFeedbackRequest(payload);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to submit support ticket';
      return rejectWithValue(message);
    }
  }
);

export const updateSupportFeedbackStatus = createAsyncThunk(
  'supportFeedback/updateStatus',
  async (payload: { id: string; status: 'pending' | 'rejected' }, { rejectWithValue }) => {
    try {
      return await updateSupportFeedbackStatusRequest(payload.id, { status: payload.status });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update support ticket';
      return rejectWithValue(message);
    }
  }
);

export const replyToSupportFeedback = createAsyncThunk(
  'supportFeedback/reply',
  async (payload: { id: string; customerReply: string }, { rejectWithValue }) => {
    try {
      return await replyToSupportFeedbackRequest(payload.id, {
        customerReply: payload.customerReply,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to send support reply';
      return rejectWithValue(message);
    }
  }
);

const supportFeedbackSlice = createSlice({
  name: 'supportFeedback',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchSupportFeedback.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSupportFeedback.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload !== null) {
          state.items = action.payload;
          state.lastFetched = Date.now();
        }
      })
      .addCase(fetchSupportFeedback.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(submitSupportFeedback.pending, state => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(submitSupportFeedback.fulfilled, (state, action) => {
        state.isSaving = false;
        state.items = sortItems([
          action.payload,
          ...state.items.filter(item => item.id !== action.payload.id),
        ]);
        state.lastFetched = Date.now();
      })
      .addCase(submitSupportFeedback.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload as string;
      })
      .addCase(updateSupportFeedbackStatus.pending, state => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(updateSupportFeedbackStatus.fulfilled, (state, action) => {
        state.isSaving = false;
        state.items = sortItems(
          state.items.map(item =>
            item.id === action.payload.id
              ? ({ ...item, ...action.payload } as SupportFeedbackSubmission)
              : item
          )
        );
        state.lastFetched = Date.now();
      })
      .addCase(updateSupportFeedbackStatus.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload as string;
      })
      .addCase(replyToSupportFeedback.pending, state => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(replyToSupportFeedback.fulfilled, (state, action) => {
        state.isSaving = false;
        state.items = sortItems(
          state.items.map(item =>
            item.id === action.payload.id
              ? ({ ...item, ...action.payload } as SupportFeedbackSubmission)
              : item
          )
        );
        state.lastFetched = Date.now();
      })
      .addCase(replyToSupportFeedback.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload as string;
      });
  },
});

export const selectSupportFeedbackItems = (state: RootState) => state.supportFeedback.items;
export const selectSupportFeedbackLoading = (state: RootState) => state.supportFeedback.isLoading;
export const selectSupportFeedbackSaving = (state: RootState) => state.supportFeedback.isSaving;
export const selectSupportFeedbackError = (state: RootState) => state.supportFeedback.error;

export default supportFeedbackSlice.reducer;
